import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { FRVEntrySummary } from '../../utils/api'
import { fmtDuration, fmtQubits } from './format'

// Categorical slots 1–3 of the validated reference palette (all-pairs CVD-safe).
const C_CHECKED = '#2a78d6'
const C_CLAIM = '#eb6834'
const C_REPLAY = '#1baf7a'
const INK_MUTED = '#64748b'
const GRID = '#e8edf3'
const SURFACE = '#ffffff'

type PointKind = 'claim' | 'checked' | 'baseline' | 'replay'

interface Pt {
  key: string
  entry: FRVEntrySummary
  kind: PointKind
  days: number
  qubits: number
  basis: string
}

const KIND_LABEL: Record<PointKind, string> = {
  claim: "Paper's claim",
  checked: 'Checked by FormalRV',
  baseline: 'Textbook baseline, checked',
  replay: "Paper's equations replayed",
}

const X_DOMAIN: [number, number] = [0.03, 4e6]
const Y_DOMAIN: [number, number] = [3e4, 1e8]
const X_TICKS: [number, string, string][] = [
  [1 / 24, '1 hour', '1 h'], [1, '1 day', '1 d'], [30.44, '1 month', '1 mo'], [365.25, '1 year', '1 yr'],
  [3652.5, '10 years', '10 yr'], [36525, '100 years', '100 yr'], [365250, '1,000 years', '1k yr'],
]
const Y_TICKS: [number, string][] = [[1e5, '100k'], [1e6, '1M'], [1e7, '10M'], [1e8, '100M']]

const M = { top: 16, right: 24, bottom: 44, left: 56 }

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(800)
  useLayoutEffect(() => {
    if (!ref.current) return
    setWidth(ref.current.clientWidth)
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width))
    ro.observe(ref.current)
    return () => ro.disconnect()
  }, [])
  return [ref, width] as const
}

function pointsOf(entries: FRVEntrySummary[]): { pts: Pt[]; arrows: [Pt, Pt][] } {
  const pts: Pt[] = []
  const arrows: [Pt, Pt][] = []
  for (const e of entries) {
    let claim: Pt | null = null
    if (e.claim) {
      claim = { key: `${e.id}:claim`, entry: e, kind: 'claim', days: e.claim.runtime_days, qubits: e.claim.physical_qubits, basis: e.claim.runtime_basis }
      pts.push(claim)
    }
    if (e.checked) {
      const p: Pt = {
        key: `${e.id}:checked`, entry: e, kind: e.kind === 'baseline' ? 'baseline' : 'checked',
        days: e.checked.runtime_days, qubits: e.checked.peak_qubits, basis: e.checked.runtime_basis,
      }
      pts.push(p)
      if (claim) arrows.push([claim, p])
    } else if (e.replay && e.replay.witnesses.length) {
      const w = e.replay.witnesses.find(w => w.physical_error === e.claim?.physical_error) ?? e.replay.witnesses[0]
      const p: Pt = { key: `${e.id}:replay`, entry: e, kind: 'replay', days: w.expected_days, qubits: w.physical_qubits, basis: 'expected time, serial witness' }
      pts.push(p)
      if (claim) arrows.push([claim, p])
    }
  }
  return { pts, arrows }
}

interface Box { x: number; y: number; w: number; h: number }
const overlaps = (a: Box, b: Box) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h

/** Greedy direct-label placement: right, left, above, below — first spot that collides with nothing. */
function placeLabels(anchors: { id: string; text: string; x: number; y: number }[], obstacles: Box[], bounds: Box) {
  const placed: Box[] = []
  const out: Record<string, { x: number; y: number; anchor: 'start' | 'end' | 'middle' }> = {}
  for (const a of anchors) {
    const w = a.text.length * 6.6 + 4
    const h = 14
    const options = [
      { x: a.x + 10, y: a.y - h / 2, anchor: 'start' as const },
      { x: a.x - 10 - w, y: a.y - h / 2, anchor: 'end' as const },
      { x: a.x - w / 2, y: a.y - 12 - h, anchor: 'middle' as const },
      { x: a.x - w / 2, y: a.y + 12, anchor: 'middle' as const },
      { x: a.x + 10, y: a.y - h - 6, anchor: 'start' as const },
      { x: a.x + 10, y: a.y + 6, anchor: 'start' as const },
    ]
    options.push(
      { x: a.x - 10 - w, y: a.y - h - 6, anchor: 'end' as const },
      { x: a.x - 10 - w, y: a.y + 6, anchor: 'end' as const },
    )
    const fits = (b: Box) => b.x >= bounds.x && b.x + b.w <= bounds.x + bounds.w && b.y >= bounds.y && b.y + b.h <= bounds.y + bounds.h
    // First choice: inside the plot and clear of everything; else inside the plot with the fewest collisions.
    const collisions = (b: Box) => [...placed, ...obstacles].filter(p => overlaps(p, b)).length
    const inside = options.filter(o => fits({ x: o.x, y: o.y, w, h }))
    const chosen = inside.find(o => collisions({ x: o.x, y: o.y, w, h }) === 0)
      ?? [...inside].sort((p, q) => collisions({ x: p.x, y: p.y, w, h }) - collisions({ x: q.x, y: q.y, w, h }))[0]
      ?? options[0]
    placed.push({ x: chosen.x, y: chosen.y, w, h })
    const tx = chosen.anchor === 'start' ? chosen.x : chosen.anchor === 'end' ? chosen.x + w : chosen.x + w / 2
    out[a.id] = { x: tx, y: chosen.y + h - 3, anchor: chosen.anchor }
  }
  return out
}

function Marker({ kind, x, y, active }: { kind: PointKind; x: number; y: number; active: boolean }) {
  const r = active ? 6.5 : 5
  switch (kind) {
    case 'claim':
      return <circle cx={x} cy={y} r={r} fill={SURFACE} stroke={C_CLAIM} strokeWidth={2.5} />
    case 'checked':
      return <circle cx={x} cy={y} r={r} fill={C_CHECKED} stroke={SURFACE} strokeWidth={2} />
    case 'baseline':
      return <rect x={x - r} y={y - r} width={2 * r} height={2 * r} rx={1.5} fill={C_CHECKED} stroke={SURFACE} strokeWidth={2} />
    case 'replay':
      return <path d={`M${x},${y - r - 1.5} L${x + r + 1.5},${y} L${x},${y + r + 1.5} L${x - r - 1.5},${y} Z`} fill={C_REPLAY} stroke={SURFACE} strokeWidth={2} />
  }
}

export function ChartLegend() {
  const item = (kind: PointKind, label: string) => (
    <span className="inline-flex items-center gap-1.5">
      <svg width="16" height="16" aria-hidden><Marker kind={kind} x={8} y={8} active={false} /></svg>
      {label}
    </span>
  )
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-qcloud-text">
      {item('claim', "Paper's claim")}
      {item('checked', 'Checked by FormalRV (resource VM)')}
      {item('replay', "Paper's equations replayed")}
      {item('baseline', 'Textbook baseline (checked)')}
      <span className="inline-flex items-center gap-1.5 text-qcloud-muted">
        <svg width="26" height="10" aria-hidden><line x1="1" y1="5" x2="19" y2="5" stroke={INK_MUTED} strokeWidth="1.5" /><path d="M18,1.5 L24,5 L18,8.5 Z" fill={INK_MUTED} /></svg>
        claim → check
      </span>
    </div>
  )
}

export default function FrontierChart({ entries }: { entries: FRVEntrySummary[] }) {
  const [wrapRef, width] = useWidth<HTMLDivElement>()
  const navigate = useNavigate()
  const [hover, setHover] = useState<string | null>(null)
  const height = Math.max(320, Math.min(440, width * 0.55))
  const iw = Math.max(200, width - M.left - M.right)
  const ih = height - M.top - M.bottom

  const sx = (d: number) => M.left + ((Math.log10(d) - Math.log10(X_DOMAIN[0])) / (Math.log10(X_DOMAIN[1]) - Math.log10(X_DOMAIN[0]))) * iw
  const sy = (q: number) => M.top + ih - ((Math.log10(q) - Math.log10(Y_DOMAIN[0])) / (Math.log10(Y_DOMAIN[1]) - Math.log10(Y_DOMAIN[0]))) * ih

  const { pts, arrows } = useMemo(() => pointsOf(entries), [entries])

  const labels = useMemo(() => {
    const primary = new Map<string, Pt>()
    for (const p of pts) {
      const prev = primary.get(p.entry.id)
      if (!prev || prev.kind === 'claim') primary.set(p.entry.id, p)
    }
    const anchors = [...primary.values()]
      .sort((a, b) => sx(a.days) - sx(b.days))
      .map(p => ({ id: p.entry.id, text: p.entry.short_name.replace(' (baseline)', ''), x: sx(p.days), y: sy(p.qubits) }))
    const obstacles = pts.map(p => ({ x: sx(p.days) - 8, y: sy(p.qubits) - 8, w: 16, h: 16 }))
    // Arrows are obstacles too, so a direct label never sits on a connector.
    for (const [a, b] of arrows) {
      const x1 = sx(a.days), y1 = sy(a.qubits), x2 = sx(b.days), y2 = sy(b.qubits)
      const steps = Math.ceil(Math.hypot(x2 - x1, y2 - y1) / 8)
      for (let i = 1; i < steps; i++) {
        const t = i / steps
        obstacles.push({ x: x1 + (x2 - x1) * t - 3, y: y1 + (y2 - y1) * t - 3, w: 6, h: 6 })
      }
    }
    return placeLabels(anchors, obstacles, { x: M.left, y: M.top, w: iw, h: ih })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pts, arrows, width, height])

  const hovered = pts.find(p => p.key === hover) ?? null

  return (
    <div ref={wrapRef} className="relative w-full">
      <svg width={width} height={height} role="img" aria-label="Physical qubits against runtime per run, log-log: each paper's claim and the figure FormalRV checked">
        {/* grid + axes */}
        {X_TICKS.map(([d, label, short], i) => (
          <g key={label}>
            <line x1={sx(d)} x2={sx(d)} y1={M.top} y2={M.top + ih} stroke={GRID} strokeWidth={1} />
            {(iw >= 420 || i % 2 === 0) && (
              <text x={sx(d)} y={M.top + ih + 16} textAnchor="middle" fontSize={11} fill={INK_MUTED}>{iw < 560 ? short : label}</text>
            )}
          </g>
        ))}
        {Y_TICKS.map(([q, label]) => (
          <g key={label}>
            <line x1={M.left} x2={M.left + iw} y1={sy(q)} y2={sy(q)} stroke={GRID} strokeWidth={1} />
            <text x={M.left - 8} y={sy(q) + 4} textAnchor="end" fontSize={11} fill={INK_MUTED} style={{ fontVariantNumeric: 'tabular-nums' }}>{label}</text>
          </g>
        ))}
        <text x={M.left + iw / 2} y={height - 6} textAnchor="middle" fontSize={11.5} fill="#1e293b" fontWeight={600}>Runtime per run (log scale)</text>
        <text transform={`translate(14 ${M.top + ih / 2}) rotate(-90)`} textAnchor="middle" fontSize={11.5} fill="#1e293b" fontWeight={600}>Physical qubits</text>

        {/* claim → check arrows */}
        <defs>
          <marker id="frv-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 Z" fill={INK_MUTED} />
          </marker>
        </defs>
        {arrows.map(([a, b]) => {
          const x1 = sx(a.days), y1 = sy(a.qubits), x2 = sx(b.days), y2 = sy(b.qubits)
          const len = Math.hypot(x2 - x1, y2 - y1)
          if (len < 18) return null
          const ux = (x2 - x1) / len, uy = (y2 - y1) / len
          return (
            <line key={`${a.key}>${b.key}`} x1={x1 + ux * 8} y1={y1 + uy * 8} x2={x2 - ux * 9} y2={y2 - uy * 9}
              stroke={INK_MUTED} strokeWidth={1.5} markerEnd="url(#frv-arrow)" opacity={hover && !hover.startsWith(a.entry.id + ':') ? 0.25 : 0.8} />
          )
        })}

        {/* marks */}
        {pts.map(p => (
          <g key={p.key} opacity={hover && !hover.startsWith(p.entry.id + ':') ? 0.35 : 1}>
            <Marker kind={p.kind} x={sx(p.days)} y={sy(p.qubits)} active={hover === p.key} />
          </g>
        ))}

        {/* direct labels (text tokens, never series colour) */}
        {Object.entries(labels).map(([id, l]) => {
          const e = entries.find(x => x.id === id)!
          return (
            <text key={id} x={l.x} y={l.y} textAnchor={l.anchor} fontSize={11.5} fontWeight={600} fill="#1e293b"
              opacity={hover && !hover.startsWith(id + ':') ? 0.35 : 1}>
              {e.short_name.replace(' (baseline)', '')}
            </text>
          )
        })}

        {/* hit targets: 24px, keyboard focusable */}
        {pts.map(p => (
          <circle key={`hit-${p.key}`} cx={sx(p.days)} cy={sy(p.qubits)} r={12} fill="transparent"
            tabIndex={0} role="button" aria-label={`${p.entry.short_name}, ${KIND_LABEL[p.kind]}: ${fmtQubits(p.qubits)} qubits, ${fmtDuration(p.days)}`}
            className="cursor-pointer outline-none"
            onPointerEnter={() => setHover(p.key)} onPointerLeave={() => setHover(null)}
            onFocus={() => setHover(p.key)} onBlur={() => setHover(null)}
            onClick={() => navigate(`/shor/${p.entry.id}`)}
            onKeyDown={ev => { if (ev.key === 'Enter') navigate(`/shor/${p.entry.id}`) }} />
        ))}
      </svg>

      {hovered && (
        <div
          className="pointer-events-none absolute z-10 bg-white border border-qcloud-border rounded-lg shadow-lg px-3 py-2 text-xs"
          style={{
            left: Math.min(sx(hovered.days) + 14, width - 220),
            top: Math.max(0, sy(hovered.qubits) - 70),
            width: 206,
          }}
        >
          <div className="text-[15px] font-semibold text-qcloud-text leading-tight">
            {fmtQubits(hovered.qubits)} qubits · {fmtDuration(hovered.days)}
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-qcloud-text">
            <svg width="12" height="12" aria-hidden><Marker kind={hovered.kind} x={6} y={6} active={false} /></svg>
            <span className="font-medium">{hovered.entry.short_name}</span>
          </div>
          <div className="text-qcloud-muted">{KIND_LABEL[hovered.kind]} · {hovered.basis}</div>
        </div>
      )}
    </div>
  )
}
